import React, { useState, useEffect } from "react";
import "../styles/SignUp.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

// ✅ Import Gradient Bold Logo
import { ReactComponent as GradientBoldLogo } from "../assets/Transparent svg/Bold Logo with Text/Gradient Bold Logo.svg";
import { googleLogin } from "../auth/authService";


const API_BASE = process.env.REACT_APP_API_BASE;

const SignUp = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [activeField, setActiveField] = useState("");
  const [showResend, setShowResend] = useState(false);
  const navigate = useNavigate();

  const [isSignedUp, setIsSignedUp] = useState(false);

  useEffect(() => {
    const hasAccount = localStorage.getItem("hasAccount");
    const token = localStorage.getItem("token");
    if (hasAccount || token) {
      setIsSignedUp(true);
    }
  }, []);


  const validateField = (name, value) => {
    let error = "";
    if (value.trim() === "") {
      error = `${name.replace(/([A-Z])/g, " $1")} is required.`;
    } else if (name === "email" && !/^\S+@\S+\.\S+$/.test(value)) {
      error = "Invalid email format.";
    } else if (name === "password" && value.length < 6) {
      error = "Password must be at least 6 characters.";
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: validateField(name, value) });
  };

  const handleFocus = (e) => setActiveField(e.target.name);
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setErrors({ ...errors, [name]: validateField(name, value) });
    setActiveField("");
  };


  const handleBackendSignup = async () => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
      });

      if (response.status >= 200 && response.status < 300) {
        localStorage.setItem("hasAccount", "true");
        navigate("/verify-notice", { state: { email: formData.email } });
      }
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Signup failed. Try again.";

      if (message.toLowerCase().includes("verify")) setShowResend(true);
      alert(message);
    }
  };


  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("hasAccount");
    localStorage.removeItem("guest");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("isGoogleUser");
    alert("You’ve been logged out.");
    setIsSignedUp(false);
    navigate("/login");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};
    Object.keys(formData).forEach((key) => {
      newErrors[key] = validateField(key, formData[key]);
    });

    setErrors(newErrors);
    if (Object.values(newErrors).some((err) => err !== "")) return;

    await handleBackendSignup();
  };

  const handleGoogleSignIn = async (response) => {
    const token = response.credential;
    try {
      const data = await googleLogin(token);

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("hasAccount", "true");

      alert("🎉 Login successful!");
      navigate("/dashboard");
    } catch (err) {
      alert(err.message || "Google login failed");
    }
  };

  useEffect(() => {
    let isMounted = true;

    const renderGoogleBtn = () => {
      const googleDiv = document.getElementById("google-btn");
      if (!isMounted) return; // 🛡 Prevents call after unmount
      if (!window.google || !window.google.accounts || !googleDiv) return;

      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleSignIn,
      });
      window.google.accounts.id.renderButton(googleDiv, {
        theme: "outline",
        size: "large",
        width: 300,
      });
    };

    const timeout = setTimeout(renderGoogleBtn, 300);

    window.addEventListener("resize", renderGoogleBtn);
    return () => {
      isMounted = false; // 🛡 mark component unmounted
      clearTimeout(timeout);
      window.removeEventListener("resize", renderGoogleBtn);
    };
  }, []);
  const handleResend = async () => {
    const email = formData.email;
    if (!email) return alert("Enter a valid email first.");
    try {
      const res = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      alert(data.message || data.error);
    } catch (err) {
      alert("Something went wrong");
    }
  };

  const isFormValid =
    Object.values(errors).every((err) => err === "") &&
    Object.values(formData).every((val) => val.trim() !== "");

  return (
    <div className="signup-container">
      <div className="signup-left">
        <img src="/log-img.png" alt="Sign Up" className="signup-image" />
        <img src="/star-icon.svg" alt="Star Icon" className="star-icon star-icon-top" />
        <img src="/blue-star-icon.svg" alt="Star Icon" className="star-icon star-icon-bottom" />
        <div className="white-circle"></div>
        <div className="signup-badge track">
          <img src="/track-icon.svg" alt="Track Icon" />
          <p>Track & Manage <br />documents on the go!</p>
        </div>
        <div className="white-circle-bottom"></div>
        <div className="white-circle-bottom-right"></div>
        <div className="white-circle-bottom-right-up"></div>
        <div className="signup-badge speed">
          <img src="/speed-icon.svg" alt="Speed Icon" />
          <p>Gain speed with <br />your document delivery 🚀</p>
        </div>
        <div className="signup-badge dispatch">
          <img src="/dispatch-icon.svg" alt="Dispatch Icon" />
          <p>Dispatch your Cheques <br />from anywhere</p>
        </div>
      </div>

      <div className="signup-right">
        <div className="signup-header">
          <GradientBoldLogo className="signup-logo-svg" />
          {/* <p className="signup-login-text">
            Already have an account?
            <span className="login-link" onClick={() => navigate("/login")}> Log in</span>
          </p> */}

          <p className="signup-login-text">
            Already have an account?
            <span
              className={`login-link ${isSignedUp ? "disabled" : ""}`}
              onClick={() => !isSignedUp && navigate("/login")}
            >
              {isSignedUp ? " Logged In" : " Log in"}
            </span>
          </p>

        </div>

        <div className="google-btn-wrapper">
          <div id="google-btn"></div>
        </div>

        <h2>Sign up as a business 🚀</h2>
        <p className="signup-subtext">Let’s create your account! Ensure you provide valid details.</p>

        <form className="signup-form" onSubmit={handleSubmit}>
          {/* ✅ FULL NAME FIELD */}
          <div
            className={`input-group ${activeField === "name" ? "active" : ""
              } ${errors.name ? "error" : ""} ${formData.name ? "filled" : ""}`}
          >
            <img src="/name-icon.svg" alt="Name Icon" className="input-icon" />
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              required
            />
          </div>


          {/* ✅ EMAIL FIELD */}
          <div className={`input-group ${activeField === "email" ? "active" : ""} ${errors.email ? "error" : ""} ${formData.email ? "filled" : ""}`}>
            <img src="/mail-icon.svg" alt="Email Icon" className="input-icon" />
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              required
            />
          </div>

          {/* ✅ PASSWORD FIELD */}
          <div className={`input-group ${activeField === "password" ? "active" : ""} ${errors.password ? "error" : ""} ${formData.password ? "filled" : ""}`}>
            <img src="/lock-icon.svg" alt="Password Icon" className="input-icon" />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              required
            />
          </div>

          {/* <button type="submit" className="sign-up-btn" disabled={!isFormValid}>
            Sign Up
          </button> */}
          {isSignedUp ? (
            <button
              type="button"
              onClick={handleLogout}
              className="sign-up-btn"
              style={{
                backgroundColor: "#fff",
                color: "#e53935",
                border: "1.5px solid #e53935",
                fontWeight: "600",
              }}
            >
              Sign Out
            </button>
          ) : (
            <button
              type="submit"
              className="sign-up-btn"
              disabled={!isFormValid}
            >
              Sign Up
            </button>
          )}

        </form>

        {showResend && (
          <button className="resend-verification-btn" onClick={handleResend}>
            ✉️ Resend Verification Email
          </button>
        )}

        {/* 🚀 Dispatch as Guest (same as login) */}
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <button
            onClick={() => {
              localStorage.setItem("guest", "true");
              localStorage.setItem("token", "guest-temp-token");
              window.location.href = "/dashboard";
            }}
            className="guest-dispatch-btn"
            style={{
              background: "transparent",
              border: "1px solid #ccc",
              borderRadius: "6px",
              padding: "10px 14px",
              width: "100%",
              color: "#444",
              cursor: "pointer",
            }}
          >
            🚀 Dispatch as Guest
          </button>
          <p style={{ fontSize: "13px", color: "#777", marginTop: "6px" }}>
            You can still receive your confirmation by email.
          </p>
        </div>

        <p className="signup-disclaimer">
          By creating or using your account, you agree to our
          <a href="/terms" className="terms-link"> Terms & Conditions</a>,
          <a href="/privacy" className="privacy-link"> Privacy Policy</a>, and
          <a href="/refund" className="privacy-link"> Refund Policy</a>.
        </p>
      </div>
    </div>
  );
};

export default SignUp;
